import { useFormik } from "formik";
import { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { updateConfig } from "../../../../store/slices/Delivery/deliveryChallanSlice";
import { createDeliveryChallan } from "../api";
import { toast } from "react-toastify";
import { delivery } from "../../../../store/queries/Delivery";
import { useGetInvoiceHistoryBasicDataQuery } from "../../../../store/queries/Billing";

const EMPTY_ITEM = {
  serial_number: "",
  description: "",
  gross_weight: "",
  net_weight: "",
  diamond_weight: "",
  stone_weight: "",
  sac_code: "998346",
  amount: "",
};

const DEFAULT_INITIAL_VALUES = {
  gli_numbers: [],
  customer_id: "",
  customer_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  contact_number: "",
  gst_number: "",
  supplier_challan_number: "",
  item_fields: [{ ...EMPTY_ITEM, serial_number: 1 }],
  remarks: "Inspection work done, Return to party",
  total_amount: "",
};

const validation = Yup.object({
  gli_numbers: Yup.array().min(1, "At least one GLI must be selected"),
  customer_name: Yup.string()
    .trim()
    .max(80, `Enter Valid Name`)
    .required(`Enter customer name`),
  address_line1: Yup.string().trim().required(`Enter address`),
  address_line2: Yup.string().trim().required(`Enter address`),
  city: Yup.string().trim().required(`Enter city name`),
  contact_number: Yup.string()
    .min(6, `Enter valid mobile number`)
    .max(14, `Enter valid mobile number`)
    .matches(/^[0-9]*$/, "Enter valid number")
    .nullable(),
  gst_number: Yup.string()
    .trim()
    .matches(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "Enter Valid GST Number",
    ),
  supplier_challan_number: Yup.string().trim(),
  remarks: Yup.string().nullable(),
  total_amount: Yup.string().matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Amount"),
  item_fields: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required(`Enter description`),
      sac_code: Yup.string()
        .matches(/^[0-9]*$/, "Enter Valid SAC Number")
        .min(6, "Enter Valid SAC Number")
        .max(20, "Enter Valid SAC Number")
        .nullable(),
      amount: Yup.string().matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Amount"),
      gross_weight: Yup.string()
        .matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Weight")
        .nullable(),
      net_weight: Yup.string()
        .matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Weight")
        .nullable(),
      diamond_weight: Yup.string()
        .matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Weight")
        .nullable(),
      stone_weight: Yup.string()
        .matches(/^[0-9]*\.?[0-9]*$/, "Enter Valid Weight")
        .nullable(),
    }),
  ),
});

const useDeliveryChallanForm = () => {
  const dispatch = useDispatch();
  const { editData, isEdit, selectedEditId, selectedRows, editDataStatus } =
    useSelector((state) => state.deliveryChallan);

  const { data: basicData = {} } = useGetInvoiceHistoryBasicDataQuery();

  const initialData = useMemo(() => {
    if (isEdit && editDataStatus === "fulfilled" && editData) {
      const data = editData?.data || editData;
      const items = (data?.items || []).map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
          newItem[key] = item[key] === null ? "" : item[key];
        });
        return newItem;
      });

      const customerList = [
        ...(basicData?.data?.supplier ?? []),
        ...(basicData?.data?.shop ?? []),
      ];
      const matchedCustomer = customerList.find(
        (c) => c?.name === data?.customer_name,
      );

      return {
        gli_numbers: data?.gli_numbers ?? [],
        customer_id: data?.customer_id ?? matchedCustomer?._id ?? "",
        customer_name: data?.customer_name ?? "",
        address_line1: data?.address?.line1 ?? "",
        address_line2: data?.address?.line2 ?? "",
        city: data?.address?.city ?? "",
        contact_number: data?.contact_number ?? "",
        gst_number: data?.gst_number ?? "",
        supplier_challan_number: data?.supplier_challan_number ?? "",
        item_fields: items.length > 0 ? items : [{ ...EMPTY_ITEM }],
        remarks: data?.remarks ?? "",
        total_amount: data?.total_amount ?? "",
      };
    }

    if (!isEdit && selectedRows?.length > 0) {
      return {
        ...DEFAULT_INITIAL_VALUES,
        gli_numbers: selectedRows,
        item_fields: [{ ...EMPTY_ITEM, serial_number: 1 }],
      };
    }

    return {
      ...DEFAULT_INITIAL_VALUES,
      item_fields: [{ ...EMPTY_ITEM, serial_number: 1 }],
    };
  }, [isEdit, editData, editDataStatus, selectedRows, basicData]);

  const formik = useFormik({
    initialValues: initialData,
    validationSchema: validation,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const data = {
        gli_numbers: values.gli_numbers,
        customer_name: values.customer_name,
        address_line1: values.address_line1,
        address_line2: values.address_line2,
        city: values.city,
        contact_number: values.contact_number,
        gst_number: values.gst_number,
        supplier_challan_number: values.supplier_challan_number,
        items: values.item_fields,
        total_amount: values.total_amount,
        remarks: values.remarks,
      };

      if (isEdit) {
        data.id = selectedEditId;
      }

      try {
        const response = await createDeliveryChallan(data);
        if (response?.data?.status_code === 200) {
          toast.success(
            response?.data?.message ||
              (isEdit
                ? "Delivery challan updated successfully"
                : "Delivery challan created successfully"),
          );
          dispatch(
            updateConfig((state) => {
              state.showOffcanvas = false;
              state.isEdit = false;
              state.selectedRows = [];
              state.selectedEditId = null;
              state.editData = {};
              state.Tab.clear = true;
            }),
          );
          dispatch(
            delivery.util.invalidateTags([
              "DeliveryChallan",
              "DeliveryChallanUploads",
            ]),
          );
          setTimeout(() => {
            dispatch(
              updateConfig((state) => {
                state.Tab.clear = false;
              }),
            );
          }, 500);
        } else if (response?.status_code === 422 && response?.errors) {
          Object.keys(response.errors).forEach((field) => {
            const rawMessage = response.errors[field];
            const message = Array.isArray(rawMessage)
              ? rawMessage[0]
              : rawMessage;
            const formikField = field.replace(
              /^items\.(\d+)\.(.+)$/,
              (_, index, subField) => `item_fields.${index}.${subField}`,
            );
            formik.setFieldTouched(formikField, true, false);
            formik.setFieldError(formikField, message);
          });
        } else {
          toast.error(response?.message || "Failed to save delivery challan");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong");
      }
    },
  });

  const totalAmount = useMemo(() => {
    let sum = 0;
    formik.values.item_fields.forEach((item) => {
      sum += parseFloat(item.amount) || 0;
    });
    return sum > 0 ? sum.toFixed(2) : "";
  }, [formik.values.item_fields]);

  if (formik.values.total_amount !== totalAmount) {
    formik.setFieldValue("total_amount", totalAmount, false);
  }

  const getFieldError = useCallback(
    (fieldName) => {
      if (formik.touched[fieldName] && formik.errors[fieldName]) {
        return formik.errors[fieldName];
      }
      return "";
    },
    [formik.touched, formik.errors],
  );

  const handleAddField = () => {
    const nextSerial = formik.values.item_fields.length + 1;
    const item_fields = [
      ...formik.values.item_fields,
      { ...EMPTY_ITEM, serial_number: nextSerial },
    ];
    formik.setFieldValue("item_fields", item_fields);
  };

  const handleRemoveGli = (index) => {
    const updatedGli = formik.values.gli_numbers.filter((_, i) => i !== index);
    formik.setFieldValue("gli_numbers", updatedGli);
    dispatch(
      updateConfig((state) => {
        state.selectedRows = updatedGli;
      }),
    );
  };

  const handleCloseModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showOffcanvas = false;
        state.isEdit = false;
        state.editData = {};
        state.selectedRows = [];
      }),
    );
  };

  return {
    formik,
    isEdit,
    basicData,
    getFieldError,
    handleAddField,
    handleRemoveGli,
    handleCloseModal,
  };
};

export default useDeliveryChallanForm;
